import { Checkbox, FormControlLabel, Grid, MenuItem, TextField } from '@material-ui/core'
import { ExpansionAutocomplete } from 'components/controls/misc/ExpansionAutocomplete'
import { Button } from 'components/facets'
import { useExpansionData } from 'components/providers/CardDataProvider'
import { startProductCatalogUpdate } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { GAME_NAMES, getGameMetaData, IdGame } from 'shared'

type SearchBy = 'product' | 'expansion'

export const ProductCatalogUpdate = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const expansions = useExpansionData()

  const [searchBy, setSearchBy] = useState<SearchBy>('product')
  const [productId, setProductId] = useState('')
  const [game, setGame] = useState('')
  const [expansionId, setExpansionId] = useState('')
  const [expansionCode, setExpansionCode] = useState('')
  // Which field drives the pair: '' = both free, 'id' = typed id, 'name' = picked from the search.
  const [expansionSource, setExpansionSource] = useState<'' | 'id' | 'name'>('')
  const [withImages, setWithImages] = useState(false)

  const [starting, setStarting] = useState(false)

  // Typing an id: the name is resolved from the card-data expansions and shown under the id field.
  const handleExpansionIdChange = (value: string) => {
    setExpansionId(value)
    setExpansionSource(value.trim() ? 'id' : '')
    setExpansionCode('')
  }

  const handleExpansionSelect = (selected: { _id: number; code: string }) => {
    if (selected && selected._id) {
      setExpansionSource('name')
      setExpansionCode(selected.code)
      setExpansionId(String(selected._id))
    } else {
      setExpansionSource('')
      setExpansionCode('')
      setExpansionId('')
    }
  }

  // Expansion names repeat across games (several Alpha), so the search lists only the chosen game's
  const handleGameChange = (value: string) => {
    setGame(value)
    if (expansionSource === 'name') handleExpansionSelect({ _id: 0, code: '' })
  }

  // Name shown under the id field, only when the id was typed (not when picked from the search).
  const typedExpansionName =
    expansionSource === 'id' && expansionId.trim()
      ? expansions.find((exp) => String(exp._id) === expansionId.trim())?.name
      : undefined
  const expansionIdHelper =
    expansionSource === 'id' && expansionId.trim()
      ? typedExpansionName ?? 'Expansion not found in catalog'
      : undefined

  const handleStart = async () => {
    const payload: { productId?: number; expansionId?: number; withImages: boolean } = {
      withImages,
    }
    if (searchBy === 'product') {
      if (!productId.trim()) {
        enqueueSnackbar('Enter a MKM product id', { variant: 'error' })
        return
      }
      payload.productId = Number(productId)
    } else {
      if (!expansionId.trim()) {
        enqueueSnackbar('Enter or select an expansion', { variant: 'error' })
        return
      }
      payload.expansionId = Number(expansionId)
    }

    try {
      setStarting(true)
      await startProductCatalogUpdate(payload)
      enqueueSnackbar('Update queued: the changes will show up in the MKM products search', {
        variant: 'success',
      })
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error ?? 'Could not start the catalog update', {
        variant: 'error',
      })
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="product-catalog-page my-3">
      <h3>Product catalog update</h3>
      <p className="text-muted">
        Refresh a single MKM product or an entire expansion from Cardmarket. The update runs in the
        background, and its changes show up in the MKM products search.
      </p>

      <div className="mb-4">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={searchBy === 'expansion' ? 2 : 3}>
            <TextField
              select
              margin="dense"
              fullWidth
              label="Search by"
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value as SearchBy)}
            >
              <MenuItem value="product">MKM product id</MenuItem>
              <MenuItem value="expansion">Expansion</MenuItem>
            </TextField>
          </Grid>

          {searchBy === 'product' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                margin="dense"
                fullWidth
                label="MKM product id"
                type="number"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder="e.g. 895551"
                // Rails can only refresh products it already has: new singles come with their expansion
                helperText="Not in the catalog yet? Refresh its expansion"
                FormHelperTextProps={{ className: 'product-catalog-id-helper' }}
              />
            </Grid>
          )}

          {searchBy === 'expansion' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  margin="dense"
                  fullWidth
                  label="Game"
                  value={game}
                  onChange={(e) => handleGameChange(e.target.value)}
                  disabled={expansionSource === 'id'}
                >
                  <MenuItem value="">All games</MenuItem>
                  {Object.entries(GAME_NAMES).map(([id, name]) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ExpansionAutocomplete
                  label="Expansion (search)"
                  value={expansionCode}
                  activeGame={game ? getGameMetaData(Number(game) as IdGame) : undefined}
                  disabled={expansionSource === 'id'}
                  onChange={handleExpansionSelect}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  margin="dense"
                  fullWidth
                  label="Expansion id"
                  type="number"
                  value={expansionId}
                  onChange={(e) => handleExpansionIdChange(e.target.value)}
                  disabled={expansionSource === 'name'}
                  placeholder="type it, or pick"
                  helperText={expansionIdHelper}
                  FormHelperTextProps={{ className: 'product-catalog-id-helper' }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={searchBy === 'expansion' ? 2 : 3}>
            <FormControlLabel
              className="mb-2"
              control={
                <Checkbox
                  color="primary"
                  checked={withImages}
                  onChange={(e) => setWithImages(e.target.checked)}
                />
              }
              label="Also update images"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={searchBy === 'expansion' ? 1 : 3}>
            <Button variant="contained" className="mb-2" onClick={handleStart} disabled={starting}>
              {starting ? 'Starting…' : 'Update'}
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
